import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material'

import { Box, Button, Icons } from '..'
import palette from '../../palette.json'

/**
 * NotificationDialog - A reusable dialog component for showing notifications of different types
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {string} type - Dialog type: 'alert', 'error', 'success', 'info', 'loading' (default: 'alert')
 * @param {string} title - Dialog title (optional, defaults based on type)
 * @param {string|React.ReactNode} message - Message to display in the dialog body
 * @param {Array} actions - Array of action button configurations
 *   Example: [{ label: "Close", onClick: handleClose, autoFocus: true, disabled: false, color: "primary", variant: "contained" }]
 *   Any additional props (disabled, color, variant, etc.) will be passed to the Button component
 * @param {string} maxWidth - Dialog max width: 'xs', 'sm', 'md', 'lg', 'xl' (default: undefined)
 */
export function NotificationDialog({ open, onClose, type = 'alert', title, message, actions = [], maxWidth }) {
  // Configuration for each dialog type
  const typeConfig = {
    alert: {
      icon: Icons.Bell,
      color: palette.alertYellow,
      defaultTitle: 'Action Required!',
    },
    error: {
      icon: Icons.Exclamation,
      color: palette.red,
      defaultTitle: 'Error',
    },
    success: {
      icon: Icons.CheckSolid,
      color: palette.success,
      defaultTitle: 'Success',
    },
    info: {
      icon: Icons.NotificationImportant,
      color: palette.blue,
      defaultTitle: 'Information',
    },
    loading: {
      icon: null,
      color: palette.blue,
      defaultTitle: 'Processing',
    },
  }

  const config = typeConfig[type] || typeConfig.alert
  const IconComponent = config.icon
  const displayTitle = title || config.defaultTitle

  // Default actions if none provided (loading type has no actions by default)
  const defaultActions = type === 'loading' ? [] : [{ label: 'Close', onClick: onClose }]
  const actionsToRender = actions.length > 0 ? actions : defaultActions

  const isLoading = type === 'loading'

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      disableEscapeKeyDown={isLoading}
      maxWidth={maxWidth}
      fullWidth={!!maxWidth}
      aria-labelledby="notification-dialog-title"
      aria-describedby="notification-dialog-description"
    >
      <DialogTitle
        id="notification-dialog-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoading ? (
            <CircularProgress size={50} sx={{ color: config.color }} />
          ) : (
            <Box
              sx={{
                color: config.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid',
                borderRadius: '50%',
                padding: '2px',
                width: '50px',
                height: '50px',
              }}
            >
              <IconComponent style={{ fontSize: '1.5em' }} />
            </Box>
          )}
          {displayTitle}
        </Box>
        {!isLoading && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'grey.500', marginTop: '-8px', marginRight: '-8px' }}
          >
            <Icons.Close />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="notification-dialog-description" component="div" sx={{ color: 'black' }}>
          {typeof message === 'string' ? (
            <span dangerouslySetInnerHTML={{ __html: message.replace(/\n/g, '<br />') }} />
          ) : (
            message
          )}
        </DialogContentText>
      </DialogContent>
      {actionsToRender.length > 0 && (
        <DialogActions>
          {actionsToRender.map((action) => {
            const { label, onClick, ...buttonProps } = action
            return (
              <Button key={label} onClick={onClick} {...buttonProps}>
                {label}
              </Button>
            )
          })}
        </DialogActions>
      )}
    </Dialog>
  )
}
